import { VARIABLE_BLOCK_CLASS } from "./variable-block";
import { VARIABLE_PILL_CLASS } from "./variable-pill";

/** Minimal rect shape so tests can feed synthetic geometry. */
export interface RectLike {
  left: number;
  top: number;
  bottom: number;
  /** Only the atom-boundary fallback needs a horizontal extent. */
  right?: number;
}

export type MeasureRects = (range: Range) => RectLike[];

const defaultMeasure: MeasureRects = (range) =>
  Array.from(range.getClientRects());

/** Pixels within which two rects count as "the same line". */
const LINE_TOLERANCE = 2;

const isZeroWidth = (text: string) => /^[​﻿\s]*$/.test(text);

const ATOM_SELECTOR = `.${VARIABLE_PILL_CLASS}, .${VARIABLE_BLOCK_CLASS}`;

const verticalDistance = (rect: RectLike, y: number) =>
  y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;

const rightEdge = (rect: RectLike) => rect.right ?? rect.left;

const horizontalDistance = (rect: RectLike, x: number) =>
  x < rect.left ? rect.left - x : x > rightEdge(rect) ? x - rightEdge(rect) : 0;

/**
 * Caret spot immediately before/after `atom`. An existing text sibling is
 * preferred over the element-level boundary: it has a real client rect, so
 * the drop indicator paints a caret line at the atom's edge instead of
 * falling back to the block's full-height bounding box. Never mutates the
 * DOM — this runs on every `dragover`.
 */
const atomBoundaryRange = (
  atom: Element,
  after: boolean,
  doc: Document,
): Range => {
  const range = doc.createRange();
  const sibling = after ? atom.nextSibling : atom.previousSibling;
  if (sibling && sibling.nodeType === Node.TEXT_NODE) {
    const text = sibling as Text;
    range.setStart(text, after ? 0 : text.length);
  } else if (after) {
    range.setStartAfter(atom);
  } else {
    range.setStartBefore(atom);
  }
  range.collapse(true);
  return range;
};

/**
 * Fallback for a root whose only measurable content is atoms — a text
 * element holding nothing but a variable pill. Pass 1 skips every text node
 * there (they are all inside the atom or zero-width guards), so there is no
 * character offset to snap to and the drag would never enter caret mode.
 * Pick the atom nearest the pointer (line first, then horizontal distance)
 * and return the boundary on the side of it the pointer is on.
 */
const nearestAtomRange = (
  root: Element,
  x: number,
  y: number,
  measure: MeasureRects,
  doc: Document,
): Range | null => {
  let best: { atom: Element; rect: RectLike; dy: number; dx: number } | null =
    null;
  for (const atom of Array.from(root.querySelectorAll(ATOM_SELECTOR))) {
    const whole = doc.createRange();
    whole.selectNodeContents(atom);
    for (const rect of measure(whole)) {
      const dy = verticalDistance(rect, y);
      const dx = horizontalDistance(rect, x);
      if (
        !best ||
        dy < best.dy - LINE_TOLERANCE ||
        (Math.abs(dy - best.dy) <= LINE_TOLERANCE && dx < best.dx)
      ) {
        best = { atom, rect, dy, dx };
      }
    }
  }
  if (!best) return null;
  const middle = (best.rect.left + rightEdge(best.rect)) / 2;
  return atomBoundaryRange(best.atom, x > middle, doc);
};

/** Elements that cannot hold a caret, so the descent below stops above them. */
const VOID_TAGS = new Set(["BR", "IMG", "HR", "INPUT", "EMBED"]);

/**
 * Last resort for a root with neither text nor atoms (a brand-new, empty text
 * element — `<p><br></p>`): the end of the deepest trailing block, which is
 * where the caret would sit and where a dropped variable belongs.
 */
export const rangeAtRootEnd = (
  root: Element,
  doc: Document = root.ownerDocument,
): Range => {
  let container: Element = root;
  while (
    container.lastElementChild &&
    !VOID_TAGS.has(container.lastElementChild.tagName)
  ) {
    container = container.lastElementChild;
  }
  const children = container.childNodes;
  let offset = children.length;
  // A trailing <br> is the editor's empty-block filler; the caret belongs
  // before it, not after it (which would render the drop on a second line).
  while (offset > 0 && children[offset - 1].nodeName === "BR") offset -= 1;
  const range = doc.createRange();
  range.setStart(container, offset);
  range.collapse(true);
  return range;
};

/**
 * Caret range nearest to a viewport point, computed from text geometry rather
 * than hit-testing.
 *
 * `document.caretRangeFromPoint` hit-tests first, and hit-testing honours
 * `pointer-events: none`. During a variable drag the editable is taken out of
 * hit-testing (so the DnD container, not Froala, receives the drop), which
 * makes the hit land on a wrapper element and the caret resolve to an
 * element-level position with no client rect — the indicator then falls back
 * to the wrapper's bounding box (a full-height bar at the left edge). This
 * walks the text nodes under `root` instead, first picking the line nearest
 * `y`, then the character offset nearest `x` on that line. Atom pills/blocks
 * and zero-width guard nodes are skipped; the caller snaps out of atoms.
 *
 * Returns null when `root` holds no measurable text (e.g. an empty editor).
 */
export const geometricCaretRange = (
  root: Element,
  x: number,
  y: number,
  measure: MeasureRects = defaultMeasure,
): Range | null => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  // Pass 1: rank text nodes by how close any of their line fragments is to y.
  const nodes: Array<{ node: Text; dy: number }> = [];
  let bestDy = Number.POSITIVE_INFINITY;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const node = n as Text;
    const text = node.data;
    if (!text || isZeroWidth(text)) continue;
    if (node.parentElement?.closest(ATOM_SELECTOR)) continue;
    const whole = doc.createRange();
    whole.selectNodeContents(node);
    const rects = measure(whole);
    if (!rects.length) continue;
    let dy = Number.POSITIVE_INFINITY;
    for (const r of rects) dy = Math.min(dy, verticalDistance(r, y));
    nodes.push({ node, dy });
    bestDy = Math.min(bestDy, dy);
  }
  if (!nodes.length) {
    return (
      nearestAtomRange(root, x, y, measure, doc) ?? rangeAtRootEnd(root, doc)
    );
  }

  // Pass 2: scan offsets only in nodes that touch the nearest line.
  let best: { node: Text; offset: number; dy: number; dx: number } | null =
    null;
  for (const { node, dy } of nodes) {
    if (dy > bestDy + LINE_TOLERANCE) continue;
    const len = node.data.length;
    for (let offset = 0; offset <= len; offset++) {
      const r = doc.createRange();
      r.setStart(node, offset);
      r.setEnd(node, offset);
      const rect = measure(r)[0];
      if (!rect) continue;
      const candDy = verticalDistance(rect, y);
      if (candDy > bestDy + LINE_TOLERANCE) continue;
      const dx = Math.abs(x - rect.left);
      if (
        !best ||
        candDy < best.dy - LINE_TOLERANCE ||
        (Math.abs(candDy - best.dy) <= LINE_TOLERANCE && dx < best.dx)
      ) {
        best = { node, offset, dy: candDy, dx };
      }
    }
  }
  if (!best) return null;

  const result = doc.createRange();
  result.setStart(best.node, best.offset);
  result.setEnd(best.node, best.offset);
  return result;
};
